import { ViewpointRelationshipsViewSourceData } from '../types';
import type { ViewSettingsForModelSourceData } from './viewSettingsPartitions';
import {
  aggregateReferences,
  DEFAULT_MULTI_LABEL_REFERENCE_FORMATTING_FILTER_ATTRIBUTES,
  getIsShowReferenceTypeApplied,
  relationshipsViewDistinctReferenceKey,
  type PureGraphEdge,
  type ScopeDataParams,
} from '@ardoq/graph';
import {
  APIOrganizationUser,
  ArdoqId,
  GroupingRule,
  ViewIds,
} from '@ardoq/api-types';
import { getScenarioGroup } from 'tabview/graphViews/traversalViewModel';
import { isTruthy } from '@ardoq/common-helpers';
import { getDefaultRelationshipDiagramsViewInterface } from 'tabview/relationshipDiagrams/viewModel$/getDefaultRelationshipDiagramsViewInterface';
import { getViewpointRelationshipsDiagramFlatEmptyState } from 'tabview/relationshipDiagrams/utils';
import type {
  ContextShape,
  LoadedGraphWithViewpointMode,
  LoadedScenarioData,
  ShowRelatedComponentsShape,
} from '@ardoq/data-model';
import type { I18nLocale } from '@ardoq/i18n';
import { getRawGraphWithScenarioData } from 'tabview/relationshipsDiagramsViewModel/getRawGraphWithScenarioData';
import {
  getPureGraphItems,
  getLabelsDataForViewsNotSupportingMultilabeling,
} from 'tabview/relationshipDiagrams/viewModel$/utils';
import { getGraphData } from 'tabview/relationshipsDiagramsViewModel/getGraphData';
import { toViewModel } from 'tabview/relationshipDiagrams/viewModel$/viewModelFromGraph';
import {
  getCandidateComponentIds,
  getColorFormattingFilterIdsForComponent,
  getColorFormattingFilterIdsForReference,
} from './utils';
import { buildComponentRepresentationDataByComponentIdFromScopeData } from 'streams/buildComponentRepresentationDataByComponentId';
import { FormattingAndLabelRules } from '@ardoq/data-model';
import { filterOperations, usersOperations } from '@ardoq/core';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { warmFilterCacheHack } from 'collections/warmFilterCacheHack';
import { buildReferenceFilterContext } from '@ardoq/filter-helpers';

const VIEW_ID = ViewIds.RELATIONSHIPS_3;

export type BuildViewpointModeSourceDataArgs = {
  context: ContextShape;
  loadedGraph: LoadedGraphWithViewpointMode;
  loadedScenarioData: LoadedScenarioData;
  scenarioRelatedComponents: ShowRelatedComponentsShape;
  showScopeRelated: boolean;
  /** Duration-tracking session the caller started, or null to not track. */
  performanceTrackingId: string | null;
  i18nLocale: I18nLocale;
  orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  viewSettings: ViewSettingsForModelSourceData;
  users: APIOrganizationUser[];
  formattingAndLabelRules: FormattingAndLabelRules['rules'];
  groupingRules: GroupingRule[];
};

export const getEmptyViewpointModeSourceData = (
  viewInstanceId: string
): ViewpointRelationshipsViewSourceData => ({
  ...getViewpointRelationshipsDiagramFlatEmptyState(VIEW_ID),
  edges: {
    byId: new Map<string, PureGraphEdge>(),
    add: [],
    update: [],
    remove: [],
  },
  viewInstanceId,
  viewId: VIEW_ID,
  users: [],
  isViewpointMode: true,
  viewInterface: getDefaultRelationshipDiagramsViewInterface(),
  colorFilterIdsByComponentId: new Map(),
  colorFilterIdsByReferenceId: new Map(),
  componentRepresentationDataByComponentId: new Map(),
});

export const buildViewpointModeSourceData = (
  previousState: ViewpointRelationshipsViewSourceData,
  {
    context,
    loadedGraph,
    loadedScenarioData,
    scenarioRelatedComponents,
    showScopeRelated,
    performanceTrackingId,
    viewSettings,
    users,
    i18nLocale,
    orgUsersById,
    formattingAndLabelRules,
    groupingRules: userDefinedGroupingRules,
  }: BuildViewpointModeSourceDataArgs
): ViewpointRelationshipsViewSourceData => {
  const enhancedScopeData = loadedGraph.scopeData;
  const graphModel = enhancedScopeData.graphData;

  const { viewInstanceId, scopeDiffState } = previousState;
  const {
    collapsedGroupIds: collapsedGroupIdsList,
    ...sourceDataViewSettings
  } = viewSettings;

  const scopeDataParams: ScopeDataParams = {
    enhancedScopeData,
    formattingFilters: formattingAndLabelRules,
    groupingRules: userDefinedGroupingRules,
    i18nLocale,
    formatUserFieldFn: val =>
      usersOperations.formatUserField(orgUsersById, val),
  };
  const viewInterface =
    getDefaultRelationshipDiagramsViewInterface(scopeDataParams);

  warmFilterCacheHack({
    scopeData: enhancedScopeData,
    scopeSource: loadedGraph,
    graphModel,
    referenceFilterContext: buildReferenceFilterContext(enhancedScopeData),
  });

  const scenarioGroup = getScenarioGroup({
    loadedScenarioData,
    showScopeRelated,
    scenarioRelatedComponentIds: scenarioRelatedComponents.componentIds,
  });

  const allGroupingRules = [scenarioGroup, ...userDefinedGroupingRules].filter(
    isTruthy
  );

  const collapsedGroupIds = new Set(collapsedGroupIdsList);
  const isInScopeDiffMode = Boolean(scopeDiffState.scopeDiff);

  const rawGraph = getRawGraphWithScenarioData({
    viewInterface,
    loadedScenarioData,
    scenarioRelatedComponents,
    showScopeRelated,
    graphModel,
    viewId: VIEW_ID,
    loadedGraph,
    isInScopeDiffMode,
    collapsedGroupIds,
    shouldCollapseGraph: false,
    userDefinedGroupingRules: allGroupingRules,
    i18nLocale,
  });

  const {
    componentMap,
    groupMap,
    referenceList,
    componentIdsOfCollapsedGroups,
    uncollapsedNodes,
    errors,
    hasClones,
  } = rawGraph;

  const getRepresentationData = (componentId: ArdoqId) =>
    enhancedScopeDataOperations.getComponentRepresentationData(
      enhancedScopeData,
      componentId
    );

  const graphData = getGraphData({
    ...rawGraph,
    viewInterface,
    getRepresentationData,
  });

  const aggregatedReferenceList = aggregateReferences({
    viewInterface,
    referenceList,
    getDistinctReferenceKey: relationshipsViewDistinctReferenceKey,
    collapsedGroupIds,
    isInScopeDiffMode,
    urlFieldValuesByReferenceId: graphData.urlFieldValuesByReferenceId,
    i18nLocale,
    ...uncollapsedNodes,
  });

  const userDefinedReferenceLabelFilters = formattingAndLabelRules.filter(
    filterOperations.isReferenceLabelFormattingFilter
  );
  const referenceLabelFilters =
    userDefinedReferenceLabelFilters.length > 0
      ? userDefinedReferenceLabelFilters
      : DEFAULT_MULTI_LABEL_REFERENCE_FORMATTING_FILTER_ATTRIBUTES;

  // labels data is indexed by graph item id
  const { nodeLabelsData, edgeLabelsData } =
    getLabelsDataForViewsNotSupportingMultilabeling({
      viewInterface,
      componentMap,
      groupMap,
      rawReferences: aggregatedReferenceList,
      isShowReferenceTypeApplied: getIsShowReferenceTypeApplied(
        referenceLabelFilters
      ),
      i18nLocale,
    });

  const { graphNodes, groupNodes, edges } = getPureGraphItems({
    i18nLocale,
    viewInterface,
    groupMap,
    componentMap,
    rawReferences: aggregatedReferenceList,
    componentIdsOfCollapsedGroups,
    rawCollapsedGroupIds: collapsedGroupIds,
    scopeDiff: scopeDiffState.scopeDiff,
    contextComponentId: context.componentId,
    nodeLabelsData,
    edgeLabelsData,
    ...graphData,
  });

  const formattingFilters = formattingAndLabelRules.filter(
    filterOperations.isStrictlyFormattingFilter
  );

  const colorFilterIdsByComponentId = new Map<ArdoqId, string[]>();
  for (const rawItem of componentMap.values()) {
    if (!rawItem.modelId) continue;
    const filterIds = getColorFormattingFilterIdsForComponent(
      enhancedScopeData,
      formattingFilters,
      rawItem.modelId
    );
    if (filterIds.length) {
      colorFilterIdsByComponentId.set(rawItem.modelId, filterIds);
    }
  }

  const colorFilterIdsByReferenceId = new Map<ArdoqId, string[]>();
  for (const referenceRawGraphItem of referenceList) {
    if (!referenceRawGraphItem.modelId) continue;
    const filterIds = getColorFormattingFilterIdsForReference(
      enhancedScopeData,
      formattingFilters,
      referenceRawGraphItem.modelId
    );
    if (filterIds.length) {
      colorFilterIdsByReferenceId.set(referenceRawGraphItem.modelId, filterIds);
    }
  }

  const nodes = toViewModel([...graphNodes.values()]);
  const groups = toViewModel([...groupNodes.values()]);
  const componentRepresentationDataByComponentId =
    buildComponentRepresentationDataByComponentIdFromScopeData(
      getCandidateComponentIds({ nodes, groups }),
      enhancedScopeData
    );

  return {
    ...previousState,
    ...graphData,
    i18nLocale,
    nodeLabelsData,
    edgeLabelsData,
    nodes,
    edges: toViewModel(edges),
    groups,
    componentRepresentationDataByComponentId,
    errors,
    hasClones,
    viewSettings: sourceDataViewSettings,
    context,
    graphModel,
    loadedScenarioData,
    scenarioRelatedComponents,
    showScopeRelated,
    rawGraph,
    performanceTrackingId,
    viewInstanceId,
    loadedGraph,
    viewInterface,
    getRepresentationData,
    users,
    formattingFilters,
    colorFilterIdsByComponentId,
    colorFilterIdsByReferenceId,
  };
};
